/**
 * AdminDashboard - Main dashboard for admin users
 *
 * Features:
 * - Real-time crawler status monitoring
 * - Review queue metrics
 * - Reddit API rate limit tracking
 * - Recent mentions overview
 * - Auto-refresh every 30 seconds
 */

import React from 'react';
import { Title, Text, Stack, SimpleGrid, Box, Alert } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { useAuth } from '../../hooks/useAuth';
import { useDashboardStats } from '../../../application/queries/useDashboard';
import { DashboardSkeleton } from '../../components/common/DashboardSkeleton';
import {
  CrawlerStatusCard,
  ReviewQueueCard,
  RateLimitCard,
  RecentMentionsList
} from '../../components/admin/Dashboard';

export const AdminDashboard: React.FC = (): React.ReactElement => {
  const { user } = useAuth();

  // Fetch dashboard data with 30-second auto-refresh
  const { data, isLoading, error } = useDashboardStats(30000);

  // Show full skeleton on initial load
  if (isLoading && !data) {
    return (
      <Stack gap="lg">
        <Box>
          <Title order={1} ff="Comfortaa, sans-serif">
            Admin Dashboard
          </Title>
          <Text c="dimmed" size="lg" mt="xs">
            Welcome, {user?.displayName ?? user?.email}!
          </Text>
        </Box>
        <DashboardSkeleton statCards={4} widgetCards={0} />
      </Stack>
    );
  }

  // Show error state
  if (error) {
    return (
      <Stack gap="lg">
        <Box>
          <Title order={1} ff="Comfortaa, sans-serif">
            Admin Dashboard
          </Title>
          <Text c="dimmed" size="lg" mt="xs">
            Welcome, {user?.displayName ?? user?.email}!
          </Text>
        </Box>
        <Alert icon={<IconInfoCircle size={16} />} title="Error" color="red">
          Failed to load dashboard data. Please try again later.
        </Alert>
      </Stack>
    );
  }

  // Render dashboard with data
  return (
    <Stack gap="lg">
      {/* Header */}
      <Box>
        <Title order={1} ff="Comfortaa, sans-serif">
          Admin Dashboard
        </Title>
        <Text c="dimmed" size="lg" mt="xs">
          Welcome, {user?.displayName ?? user?.email}!
        </Text>
        <Text c="dimmed" size="sm" mt={4}>
          Auto-refreshing every 30 seconds
        </Text>
      </Box>

      {/* Dashboard Grid - 2x2 Layout */}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
        {/* Row 1 */}
        <CrawlerStatusCard status={data!.crawlerStatus} isLoading={isLoading} />
        <ReviewQueueCard reviewQueue={data!.reviewQueue} isLoading={isLoading} />

        {/* Row 2 */}
        <RateLimitCard rateLimit={data!.rateLimit} isLoading={isLoading} />
        <RecentMentionsList mentions={data!.recentMentions} isLoading={isLoading} />
      </SimpleGrid>
    </Stack>
  );
};
